import React, { FC, useEffect, useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseIcon from '@mui/icons-material/Close'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import { IHeatmapRequest } from '../../../../shared/types/api/heatmap.type'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onUpdate: (updatedParams: IHeatmapRequest) => void
  initialValues: IHeatmapRequest
}

type HeatmapSection = 'transmitter' | 'receiver' | 'antenna' | 'output'

const COMMON_ANTENNA_PATTERNS = ['omni', 'cosine'] as const
type CommonAntennaPattern = (typeof COMMON_ANTENNA_PATTERNS)[number]
const isCommonAntennaPattern = (value: string): value is CommonAntennaPattern =>
  COMMON_ANTENNA_PATTERNS.includes(value as CommonAntennaPattern)

const HeatmapSettingsModal: FC<IProps> = ({ isOpen, onClose, onUpdate, initialValues }) => {
  const [formData, setFormData] = useState<IHeatmapRequest>(initialValues)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    setFormData(initialValues)
  }, [initialValues])

  useEffect(() => {
    const newErrors: Record<string, string> = {}

    if (formData.transmitter.alt > 1000) newErrors['transmitter.alt'] = 'Maximum altitude exceeded (1000 m)'

    if (formData.transmitter.frq < 30 || formData.transmitter.frq > 50000)
      newErrors['transmitter.frq'] = 'Frequency must be between 30 and 50,000 MHz'

    if (formData.output.rad <= 0 || formData.output.rad > 1000)
      newErrors['output.rad'] = 'Radius must be between 0 and 1,000 km'

    // Empty custom pattern name silently falls back to the cosine default on the
    // backend, so block submit until the user enters one.
    if (typeof formData.antenna.ant === 'string' && formData.antenna.ant.trim() === '')
      newErrors['antenna.ant'] = 'Custom pattern name is required'

    setErrors((prev) => {
      const hasChanges =
        Object.keys(newErrors).some((k) => newErrors[k] !== prev[k]) || Object.keys(prev).some((k) => !newErrors[k])
      return hasChanges ? newErrors : prev
    })
  }, [formData])

  const setNestedField = (section: HeatmapSection, field: string, value: string | number | null) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] as object),
        [field]: value,
      },
    }))
  }

  // For numeric fields: empty string → null, otherwise parse as number.
  const handleNumberChange = (section: HeatmapSection, field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setNestedField(section, field, value === '' ? null : +value)
  }

  // For plain text fields (e.g. antenna pattern name).
  const handleTextChange = (section: HeatmapSection, field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setNestedField(section, field, e.target.value)
  }

  const handlePolChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target
    setNestedField('antenna', 'pol', value === '' ? null : value)
  }

  const handlePatternChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target

    if (value === 'custom') {
      const currentPattern = typeof formData.antenna.ant === 'string' ? formData.antenna.ant.trim() : ''
      setNestedField('antenna', 'ant', currentPattern && !isCommonAntennaPattern(currentPattern) ? currentPattern : '')
      return
    }

    setNestedField('antenna', 'ant', value)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onUpdate(formData)
    onClose()
  }

  const numField = (
    label: string,
    section: HeatmapSection,
    field: string,
    options: { disabled?: boolean; helperText?: string } = {},
  ) => (
    <TextField
      fullWidth
      label={label}
      type="number"
      value={(formData[section] as any)[field] ?? ''}
      onChange={handleNumberChange(section, field)}
      error={!!errors[`${section}.${field}`]}
      helperText={errors[`${section}.${field}`] ?? options.helperText}
      disabled={options.disabled}
      margin="normal"
      size="small"
    />
  )

  const textField = (label: string, section: HeatmapSection, field: string, helperText?: string) => (
    <TextField
      fullWidth
      label={label}
      value={(formData[section] as any)[field] ?? ''}
      onChange={handleTextChange(section, field)}
      error={!!errors[`${section}.${field}`]}
      helperText={errors[`${section}.${field}`] ?? helperText}
      margin="normal"
      size="small"
    />
  )

  const hasErrors = Object.values(errors).some(Boolean)
  const antennaPattern = typeof formData.antenna.ant === 'string' ? formData.antenna.ant.trim() : ''
  const isCommonPattern = isCommonAntennaPattern(antennaPattern)
  const patternSelectValue = isCommonPattern ? antennaPattern : 'custom'
  const showCosineFields = antennaPattern === 'cosine'

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 440,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">Heatmap Parameters</Typography>
          <IconButton aria-label="Close heatmap parameters" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {hasErrors && (
          <Box sx={{ mb: 1 }}>
            {Object.entries(errors).map(([key, msg]) =>
              msg ? (
                <Typography key={key} variant="body2" color="error" sx={{ fontSize: 12 }}>
                  • {msg}
                </Typography>
              ) : null,
            )}
          </Box>
        )}

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {/* Transmitter — location + RF source params */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Transmitter</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {numField('Latitude', 'transmitter', 'lat', { disabled: true })}
              {numField('Longitude', 'transmitter', 'lon', { disabled: true })}
              {numField('Height above ground (m)', 'transmitter', 'alt', {
                helperText: 'Meters AGL, not sea level',
              })}
              {numField('Frequency (MHz)', 'transmitter', 'frq')}
              {numField('Transmit power (W)', 'transmitter', 'txw')}
              {numField('Bandwidth (MHz)', 'transmitter', 'bwi')}
            </AccordionDetails>
          </Accordion>

          {/* Antenna — radiation pattern and pointing */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Antenna</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl fullWidth margin="normal" size="small">
                <InputLabel>Antenna pattern</InputLabel>
                <Select value={patternSelectValue} label="Antenna pattern" onChange={handlePatternChange}>
                  <MenuItem value="omni">Omnidirectional</MenuItem>
                  <MenuItem value="cosine">Directional (cosine)</MenuItem>
                  <MenuItem value="custom">Custom BrightRF pattern</MenuItem>
                </Select>
              </FormControl>
              {patternSelectValue === 'custom' &&
                textField(
                  'Custom pattern name',
                  'antenna',
                  'ant',
                  'Use the BrightRF pattern name from the device database when available.',
                )}
              {numField('Gain (dBi)', 'antenna', 'txg')}
              {numField('Line / cable loss (dB)', 'antenna', 'txl')}
              {numField('Azimuth (°)', 'antenna', 'azi', { helperText: '0 = true north, clockwise' })}
              {numField('Tilt (°)', 'antenna', 'tlt', { helperText: '0 = horizontal, positive = downtilt' })}
              {showCosineFields && (
                <>
                  {numField('Horizontal beamwidth (°)', 'antenna', 'hbw', {
                    helperText: 'Main-lobe width for the cosine directional pattern',
                  })}
                  {numField('Vertical beamwidth (°)', 'antenna', 'vbw', {
                    helperText: 'Vertical main-lobe width for the cosine directional pattern',
                  })}
                  {numField('Front-to-back ratio (dB)', 'antenna', 'fbr', {
                    helperText: 'Rear rejection relative to forward gain for the cosine pattern',
                  })}
                </>
              )}
              <FormControl fullWidth margin="normal" size="small">
                <InputLabel>Polarization</InputLabel>
                <Select value={formData.antenna.pol ?? ''} label="Polarization" onChange={handlePolChange}>
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="v">Vertical</MenuItem>
                  <MenuItem value="h">Horizontal</MenuItem>
                </Select>
              </FormControl>
            </AccordionDetails>
          </Accordion>

          {/* Receiver — ground unit height */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Receiver</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {numField('Height above ground (m)', 'receiver', 'alt', { helperText: 'Meters AGL' })}
            </AccordionDetails>
          </Accordion>

          {/* Coverage — analysis area */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Coverage</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {numField('Analysis radius (km)', 'output', 'rad')}
              {numField('Resolution (m)', 'output', 'res', { helperText: 'Grid spacing for the heatmap' })}
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={hasErrors}>
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default HeatmapSettingsModal
